import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Smartphone, Zap, Wifi, Tv, Droplets, CreditCard, CheckCircle, XCircle, Loader2, FileText, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const CATEGORY_ICONS = {
  'pulsa': Smartphone,
  'data': Wifi,
  'pln': Zap,
  'pdam': Droplets,
  'tv': Tv,
  'voucher': CreditCard
};

export default function PPOBPage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customerNumber, setCustomerNumber] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, txRes] = await Promise.all([
        api.get('/tenant/ppob/categories'),
        api.get('/tenant/ppob/orders')
      ]);
      // Map categories to have 'code' field for consistency
      const cats = (catRes.data.categories || []).map(c => ({
        ...c,
        code: c.id?.toString() || c.type || 'pulsa'
      }));
      setCategories(cats);
      setTransactions(txRes.data.orders || []);
      
      if (cats.length > 0) {
        setSelectedCategory(cats[0].code);
        fetchProducts(cats[0].code);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat data PPOB', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (categoryCode) => {
    try {
      const res = await api.get(`/tenant/ppob/products?category=${categoryCode}`);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const handleCategoryChange = (code) => {
    setSelectedCategory(code);
    fetchProducts(code);
  };

  const handlePurchase = async () => {
    if (!selectedProduct || !customerNumber) {
      toast({ title: 'Error', description: 'Pilih produk dan isi nomor pelanggan', variant: 'destructive' });
      return;
    }
    
    setPurchasing(true);
    setPurchaseResult(null);
    
    try {
      const res = await api.post('/tenant/ppob/order', {
        sku: selectedProduct.sku || selectedProduct.code,
        customer_number: customerNumber,
        customer_name: '',
        customer_email: ''
      });
      
      setPurchaseResult(res.data.order);
      toast({ title: 'Berhasil', description: 'Transaksi berhasil diproses' });
      fetchData(); // Refresh transactions
    } catch (err) {
      setPurchaseResult({ status: 'failed', message: err.response?.data?.detail || 'Transaksi gagal' });
      toast({ title: 'Gagal', description: err.response?.data?.detail || 'Transaksi gagal', variant: 'destructive' });
    } finally {
      setPurchasing(false);
    }
  };

  const downloadReceipt = async (transactionId) => {
    try {
      const response = await api.get(`/tenant/ppob/receipt/${transactionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${transactionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal download struk', variant: 'destructive' });
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const getStatusBadge = (status) => {
    const statusMap = {
      'success': { label: 'Sukses', variant: 'default', className: 'bg-emerald-500' },
      'pending': { label: 'Pending', variant: 'secondary', className: 'bg-amber-500' },
      'failed': { label: 'Gagal', variant: 'destructive', className: 'bg-red-500' }
    };
    const s = statusMap[status] || statusMap.pending;
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div data-testid="ppob-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">PPOB - Layanan Digital</h1>
        <p className="text-muted-foreground">Pulsa, Token PLN, Paket Data, dan layanan digital lainnya</p>
      </div>

      <Tabs defaultValue="purchase">
        <TabsList>
          <TabsTrigger value="purchase">Transaksi Baru</TabsTrigger>
          <TabsTrigger value="history">Riwayat ({transactions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="purchase" className="space-y-6">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const catType = (cat.type || cat.code || '').toLowerCase().split('-')[0];
              const Icon = CATEGORY_ICONS[catType] || CreditCard;
              return (
                <Button
                  key={cat.id || cat.code}
                  variant={selectedCategory === cat.code ? 'default' : 'outline'}
                  onClick={() => handleCategoryChange(cat.code)}
                  className={selectedCategory === cat.code ? 'bg-[#2D6CDF]' : ''}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {cat.name}
                </Button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Product List */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Pilih Produk</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {products.map((product) => (
                      <button
                        key={product.sku || product.code}
                        onClick={() => setSelectedProduct(product)}
                        className={`p-4 border rounded-lg text-left transition-all ${
                          selectedProduct?.sku === product.sku 
                            ? 'border-[#2D6CDF] bg-blue-50 ring-2 ring-[#2D6CDF]' 
                            : 'hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <p className="font-medium text-sm truncate">{product.name}</p>
                        <p className="text-lg font-bold text-[#2D6CDF] mt-1">Rp {fmt(product.selling_price || product.price)}</p>
                        {product.profit > 0 && (
                          <p className="text-xs text-emerald-600">Profit: Rp {fmt(product.profit)}</p>
                        )}
                      </button>
                    ))}
                    {products.length === 0 && (
                      <div className="col-span-full py-8 text-center text-muted-foreground">
                        Tidak ada produk tersedia untuk kategori ini
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Purchase Form */}
            <div>
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle>Form Pembelian</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedProduct ? (
                    <>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="font-medium">{selectedProduct.name}</p>
                        <p className="text-2xl font-bold text-[#2D6CDF]">Rp {fmt(selectedProduct.selling_price || selectedProduct.price)}</p>
                      </div>
                      
                      <div>
                        <Label>Nomor Pelanggan</Label>
                        <Input 
                          placeholder="Contoh: 08123456789"
                          value={customerNumber}
                          onChange={(e) => setCustomerNumber(e.target.value)}
                          data-testid="ppob-customer-number"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedProduct.code?.includes('pln') ? 'Masukkan nomor meter/ID Pelanggan PLN' : 
                           selectedProduct.code?.includes('pulsa') ? 'Masukkan nomor HP' :
                           'Masukkan nomor tujuan'}
                        </p>
                      </div>
                      
                      <Button 
                        className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]"
                        onClick={() => setShowPurchaseDialog(true)}
                        disabled={!customerNumber}
                        data-testid="ppob-purchase-btn"
                      >
                        Beli Sekarang
                      </Button>
                    </>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      Pilih produk terlebih dahulu
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Riwayat Transaksi PPOB</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">No. Ref</th>
                      <th className="text-left py-3 px-4">Produk</th>
                      <th className="text-left py-3 px-4">No. Pelanggan</th>
                      <th className="text-left py-3 px-4">Harga</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">SN</th>
                      <th className="text-left py-3 px-4">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="border-b">
                        <td className="py-3 px-4 font-mono text-sm">{tx.provider_ref || tx.id?.slice(0, 8)}</td>
                        <td className="py-3 px-4">{tx.product_name || tx.sku}</td>
                        <td className="py-3 px-4">{tx.customer_number}</td>
                        <td className="py-3 px-4">Rp {fmt(tx.selling_price || tx.amount)}</td>
                        <td className="py-3 px-4">{getStatusBadge(tx.status)}</td>
                        <td className="py-3 px-4 font-mono text-xs">{tx.sn || '-'}</td>
                        <td className="py-3 px-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadReceipt(tx.id)}
                          >
                            <Download className="h-3 w-3 mr-1" /> Struk
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {transactions.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-muted-foreground">
                          Belum ada transaksi
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Pembelian</DialogTitle>
          </DialogHeader>
          
          {purchaseResult ? (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg flex items-center gap-3 ${
                purchaseResult.status === 'success' ? 'bg-emerald-50' : 
                purchaseResult.status === 'failed' ? 'bg-red-50' : 'bg-amber-50'
              }`}>
                {purchaseResult.status === 'success' ? (
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                ) : purchaseResult.status === 'failed' ? (
                  <XCircle className="h-8 w-8 text-red-500" />
                ) : (
                  <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
                )}
                <div>
                  <p className="font-medium">
                    {purchaseResult.status === 'success' ? 'Transaksi Berhasil!' :
                     purchaseResult.status === 'failed' ? 'Transaksi Gagal' : 'Memproses...'}
                  </p>
                {purchaseResult.sn && (
                    <p className="text-sm">SN: <span className="font-mono">{purchaseResult.sn}</span></p>
                  )}
                </div>
              </div>
              
              {purchaseResult.status === 'success' && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">No. Ref:</span>
                    <span className="font-mono">{purchaseResult.provider_ref || purchaseResult.id?.slice(0, 8)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Produk:</span>
                    <span>{purchaseResult.product_name || purchaseResult.sku}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">No. Tujuan:</span>
                    <span>{purchaseResult.customer_number}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Total:</span>
                    <span>Rp {fmt(purchaseResult.selling_price || purchaseResult.amount)}</span>
                  </div>
                </div>
              )}
              
              <Button 
                className="w-full"
                onClick={() => {
                  setShowPurchaseDialog(false);
                  setPurchaseResult(null);
                  setSelectedProduct(null);
                  setCustomerNumber('');
                }}
              >
                Tutup
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Produk:</span>
                  <span className="font-medium">{selectedProduct?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">No. Tujuan:</span>
                  <span className="font-mono">{customerNumber}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-[#2D6CDF]">Rp {fmt(selectedProduct?.selling_price || selectedProduct?.price)}</span>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPurchaseDialog(false)}>Batal</Button>
                <Button 
                  className="bg-[#2D6CDF] hover:bg-[#1A5BC4]"
                  onClick={handlePurchase}
                  disabled={purchasing}
                >
                  {purchasing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Konfirmasi Pembelian
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
